import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Package,
  Users,
  ShoppingBag,
  FileText,
  TrendingUp,
  AlertTriangle,
  MessageCircle,
  Palette,
  Sun,
  Moon,
  Sparkles,
  HandCoins,
  AlertCircle,
  ArrowUpRight,
  ArrowDownLeft
} from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { toast } from 'sonner';
import { formatCurrency, fmtNum } from '@/utils/currency';

const CHART_COLORS = ['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#6366f1', '#ec4899', '#14b8a6'];

const THEMES = {
  light: {
    key: 'light',
    label: 'Light',
    icon: Sun,
    bg: 'bg-slate-50',
    cardBg: 'bg-white border-slate-200',
    text: 'text-slate-900',
    subtext: 'text-slate-600',
    accent: 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200',
    quickCard: 'bg-white border border-slate-200 shadow-sm hover:shadow-md',
  },
  dark: {
    key: 'dark',
    label: 'Dark',
    icon: Moon,
    bg: 'bg-slate-950',
    cardBg: 'bg-slate-900/80 border-slate-700/50',
    text: 'text-white',
    subtext: 'text-slate-400',
    accent: 'bg-slate-900/60 border-slate-700/50',
    quickCard: 'bg-slate-800/60 border border-slate-700/50 hover:bg-slate-800',
  },
  glass: {
    key: 'glass',
    label: 'Glass Thermal',
    icon: Sparkles,
    bg: 'bg-gradient-to-br from-slate-950 via-indigo-950 to-violet-950',
    cardBg: 'bg-white/5 backdrop-blur-xl border-white/10',
    text: 'text-white',
    subtext: 'text-white/60',
    accent: 'bg-white/5 backdrop-blur-xl border-white/10',
    quickCard: 'bg-white/5 backdrop-blur-xl border border-white/10 hover:bg-white/10',
  },
};

const THERMAL_GRADIENTS = [
  'from-rose-500 via-orange-500 to-amber-400',
  'from-violet-500 via-fuchsia-500 to-pink-400',
  'from-cyan-500 via-blue-500 to-indigo-400',
  'from-emerald-500 via-teal-400 to-cyan-400',
  'from-amber-500 via-orange-500 to-red-400',
  'from-blue-500 via-indigo-500 to-violet-400',
];

export default function Dashboard() {
  const { token, user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [showCustomers, setShowCustomers] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [pendingPettyCash, setPendingPettyCash] = useState(0);
  const [loanSummary, setLoanSummary] = useState(null);
  const [loanTrend, setLoanTrend] = useState([]);
  const [theme, setTheme] = useState(() => localStorage.getItem('dashboard-theme') || 'light');

  const t = THEMES[theme] || THEMES.light;
  const isGlass = theme === 'glass';
  const isDark = theme === 'dark' || theme === 'glass';

  const canApproveVerify = user?.permissions?.includes('pettycash_approve_verify') || user?.permissions?.includes('all');
  const isAdmin = user?.permissions?.includes('all') || user?.role === 'Admin';

  useEffect(() => {
    fetchStats();
    fetchCompanyLogo();
    if (isAdmin) {
      fetchLoanSummary();
      fetchLoanTrend();
    }
    if (canApproveVerify) {
      fetchPendingPettyCash();
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('dashboard-theme', theme);
  }, [theme]);

  const fetchPendingPettyCash = async () => {
    try {
      const response = await axios.get(`${API}/petty-cash/pending-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingPettyCash(response.data.count);
    } catch (error) {
      console.error('Failed to fetch pending petty cash count');
    }
  };

  const fetchCompanyLogo = async () => {
    try {
      const response = await axios.get(`${API}/settings/company`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.company_logo) {
        setCompanyLogo(response.data.company_logo);
      }
    } catch (error) {
      console.error('Failed to load company logo');
    }
  };

  const fetchLoanSummary = async () => {
    try {
      const response = await axios.get(`${API}/loans/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoanSummary(response.data);
    } catch (error) {
      console.error('Failed to load loan summary');
    }
  };

  const fetchLoanTrend = async () => {
    try {
      const response = await axios.get(`${API}/loans/monthly-trend`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoanTrend(response.data);
    } catch (error) {
      console.error('Failed to load loan trend');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
      setShowCustomers(true);
    } catch (error) {
      toast.error('Failed to load customers');
    }
  };

  const statCards = [
    {
      title: 'Total Products',
      value: stats?.total_products || 0,
      icon: Package,
      gradient: isGlass ? THERMAL_GRADIENTS[0] : 'from-blue-500 to-blue-600',
      bgColor: isDark ? 'bg-blue-500/20' : 'bg-blue-50',
      iconColor: isDark ? 'text-blue-400' : 'text-blue-600',
      link: '/inventory'
    },
    {
      title: 'Total Customers',
      value: stats?.total_customers || 0,
      icon: Users,
      gradient: isGlass ? THERMAL_GRADIENTS[1] : 'from-purple-500 to-purple-600',
      bgColor: isDark ? 'bg-purple-500/20' : 'bg-purple-50',
      iconColor: isDark ? 'text-purple-400' : 'text-purple-600',
      link: null
    },
    {
      title: 'Total Sales',
      value: stats?.total_sales || 0,
      icon: ShoppingBag,
      gradient: isGlass ? THERMAL_GRADIENTS[2] : 'from-emerald-500 to-emerald-600',
      bgColor: isDark ? 'bg-emerald-500/20' : 'bg-emerald-50',
      iconColor: isDark ? 'text-emerald-400' : 'text-emerald-600',
      link: '/sales-history'
    },
    {
      title: 'Pending Invoices',
      value: stats?.pending_invoices || 0,
      icon: FileText,
      gradient: isGlass ? THERMAL_GRADIENTS[3] : 'from-orange-500 to-orange-600',
      bgColor: isDark ? 'bg-orange-500/20' : 'bg-orange-50',
      iconColor: isDark ? 'text-orange-400' : 'text-orange-600',
      link: '/invoices'
    },
    {
      title: 'Total Revenue',
      value: formatCurrency(stats?.total_revenue || 0),
      icon: TrendingUp,
      gradient: isGlass ? THERMAL_GRADIENTS[4] : 'from-teal-500 to-teal-600',
      bgColor: isDark ? 'bg-teal-500/20' : 'bg-teal-50',
      iconColor: isDark ? 'text-teal-400' : 'text-teal-600',
      link: '/sales-reports'
    },
    {
      title: 'Low Stock Items',
      value: stats?.low_stock_count || 0,
      icon: AlertTriangle,
      gradient: isGlass ? THERMAL_GRADIENTS[5] : 'from-red-500 to-red-600',
      bgColor: isDark ? 'bg-red-500/20' : 'bg-red-50',
      iconColor: isDark ? 'text-red-400' : 'text-red-600',
      link: '/inventory'
    },
  ];

  const visibleCards = isAdmin ? statCards : statCards.filter(c => c.title !== 'Total Revenue');

  return (
    <Layout>
      <div className={`min-h-screen ${t.bg} transition-colors duration-500`}>
        <div className="p-6 lg:p-8 space-y-8" data-testid="dashboard">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div className="flex-1 text-center">
              {companyLogo ? (
                <div className="flex justify-center mb-2">
                  <img
                    src={companyLogo}
                    alt="Company Logo"
                    className={`h-24 w-auto object-contain ${isDark ? 'brightness-0 invert' : ''}`}
                    style={isDark ? { filter: 'brightness(0) invert(1) drop-shadow(0 0 20px rgba(255,255,255,0.6)) drop-shadow(0 0 40px rgba(255,255,255,0.3))' } : {}}
                  />
                </div>
              ) : (
                <h1 className={`text-3xl font-bold ${t.text}`}>ASSETCO</h1>
              )}
              <p className={`${t.subtext} mt-1`}>Welcome back! Here's your business overview</p>
            </div>

            <div className="flex items-center gap-2">
              {/* Theme Switcher */}
              <div className={`flex gap-1 p-1 rounded-lg ${isDark ? 'bg-white/10' : 'bg-slate-100'}`} data-testid="theme-switcher">
                {Object.values(THEMES).map((th) => {
                  const ThIcon = th.icon;
                  return (
                    <button
                      key={th.key}
                      onClick={() => setTheme(th.key)}
                      className={`p-2 rounded-md transition-all ${
                        theme === th.key
                          ? (isDark ? 'bg-white/20 text-white' : 'bg-white text-slate-900 shadow-sm')
                          : (isDark ? 'text-white/50 hover:text-white/80' : 'text-slate-400 hover:text-slate-600')
                      }`}
                      title={th.label}
                      data-testid={`theme-${th.key}`}
                    >
                      <ThIcon className="w-4 h-4" />
                    </button>
                  );
                })}
              </div>

              {/* Petty Cash Notification */}
              {canApproveVerify && pendingPettyCash > 0 && (
                <a href="/petty-cash" className="relative">
                  <div className={`${isDark ? 'bg-blue-500/20' : 'bg-blue-100'} p-4 rounded-lg hover:opacity-80 transition-all cursor-pointer`}>
                    <MessageCircle className={`w-8 h-8 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                      {pendingPettyCash}
                    </span>
                  </div>
                </a>
              )}
            </div>
          </div>

          {/* Stats Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className={`p-6 animate-pulse ${t.cardBg}`}>
                  <div className={`h-20 ${isDark ? 'bg-white/10' : 'bg-slate-200'} rounded`}></div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visibleCards.map((stat, index) => {
                const Icon = stat.icon;
                const isCustomerCard = stat.title === 'Total Customers';
                const hasLink = stat.link || isCustomerCard;

                const handleClick = () => {
                  if (isCustomerCard) {
                    fetchCustomers();
                  } else if (stat.link) {
                    window.location.href = stat.link;
                  }
                };

                return (
                  <Card
                    key={index}
                    className={`p-6 ${t.cardBg} hover:shadow-lg transition-all duration-300 ${hasLink ? 'cursor-pointer' : ''} ${
                      isGlass ? 'hover:bg-white/10' : ''
                    }`}
                    data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={hasLink ? handleClick : undefined}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className={`text-sm font-medium ${t.subtext} mb-1`}>{stat.title}</p>
                        <p className={`text-3xl font-bold ${t.text}`}>{stat.value}</p>
                        {hasLink && (
                          <p className={`text-xs mt-1 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>Click to view</p>
                        )}
                      </div>
                      <div className={`w-12 h-12 ${stat.bgColor} rounded-xl flex items-center justify-center ${
                        isGlass ? 'ring-1 ring-white/10' : ''
                      }`}>
                        <Icon className={`w-6 h-6 ${stat.iconColor}`} />
                      </div>
                    </div>
                    {isGlass && (
                      <div className={`mt-3 h-1 rounded-full bg-gradient-to-r ${stat.gradient} opacity-60`} />
                    )}
                  </Card>
                );
              })}
            </div>
          )}

          {/* Loan Summary Widget + Overdue Alerts (Admin Only) */}
          {isAdmin && loanSummary && (loanSummary.active_loans_count > 0 || loanSummary.active_borrowings_count > 0) && (
            <div className="space-y-4" data-testid="loan-summary-widget">
              {/* Overdue Alert */}
              {loanSummary.overdue_count > 0 && (
                <Card className={`p-4 border ${isDark ? 'border-red-500/40 bg-red-500/10' : 'border-red-300 bg-red-50'}`} data-testid="dashboard-overdue-alert">
                  <div className="flex items-start gap-3">
                    <AlertCircle className={`w-5 h-5 ${isDark ? 'text-red-400' : 'text-red-600'} mt-0.5 flex-shrink-0`} />
                    <div className="flex-1">
                      <h3 className={`text-sm font-semibold ${isDark ? 'text-red-300' : 'text-red-800'}`}>
                        {loanSummary.overdue_count} Overdue {loanSummary.overdue_count === 1 ? 'Loan' : 'Loans'}
                      </h3>
                      <div className="mt-1 space-y-0.5">
                        {loanSummary.overdue_items.slice(0, 3).map(item => (
                          <p key={item.id} className={`text-xs ${isDark ? 'text-red-400/80' : 'text-red-700'}`}>
                            <span className="font-medium">{item.person}</span> — {item.currency} {fmtNum(item.remaining)} remaining, due {new Date(item.due_date).toLocaleDateString()}
                          </p>
                        ))}
                        {loanSummary.overdue_count > 3 && (
                          <p className={`text-xs ${isDark ? 'text-red-400/60' : 'text-red-600'}`}>+{loanSummary.overdue_count - 3} more</p>
                        )}
                      </div>
                      <a href="/loans" className={`text-xs font-medium mt-2 inline-block ${isDark ? 'text-red-300 hover:text-red-200' : 'text-red-700 hover:text-red-900'}`}>
                        View all overdue loans &rarr;
                      </a>
                    </div>
                  </div>
                </Card>
              )}

              {/* Due Soon Alert */}
              {loanSummary.due_soon_count > 0 && (
                <Card className={`p-4 border ${isDark ? 'border-amber-500/40 bg-amber-500/10' : 'border-amber-300 bg-amber-50'}`} data-testid="dashboard-due-soon-alert">
                  <div className="flex items-start gap-3">
                    <AlertCircle className={`w-5 h-5 ${isDark ? 'text-amber-400' : 'text-amber-600'} mt-0.5 flex-shrink-0`} />
                    <div className="flex-1">
                      <h3 className={`text-sm font-semibold ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                        {loanSummary.due_soon_count} Due Soon
                      </h3>
                      <div className="mt-1 space-y-0.5">
                        {loanSummary.due_soon_items.slice(0, 3).map(item => (
                          <p key={item.id} className={`text-xs ${isDark ? 'text-amber-400/80' : 'text-amber-700'}`}>
                            <span className="font-medium">{item.person}</span> — {item.currency} {fmtNum(item.remaining)} remaining, due {new Date(item.due_date).toLocaleDateString()}
                          </p>
                        ))}
                        {loanSummary.due_soon_count > 3 && (
                          <p className={`text-xs ${isDark ? 'text-amber-400/60' : 'text-amber-600'}`}>+{loanSummary.due_soon_count - 3} more</p>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {/* Loan Summary Card */}
              <Card className={`p-5 ${t.cardBg} transition-all duration-300`} data-testid="loan-summary-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-amber-500/20' : 'bg-amber-50'}`}>
                      <HandCoins className={`w-4 h-4 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                    </div>
                    <h3 className={`text-sm font-semibold ${t.text}`}>Loans & Borrowings</h3>
                  </div>
                  <a href="/loans" className={`text-xs font-medium ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}>
                    View all &rarr;
                  </a>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-blue-50'}`}>
                    <div className="flex items-center gap-1.5 mb-1">
                      <ArrowUpRight className={`w-3.5 h-3.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                      <span className={`text-xs font-medium ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>Loans (Lent)</span>
                    </div>
                    {loanSummary.currency_breakdown_loans && Object.keys(loanSummary.currency_breakdown_loans).length > 0 ? (
                      <div className="space-y-0.5">
                        {Object.entries(loanSummary.currency_breakdown_loans).map(([cur, data]) => (
                          <p key={cur} className={`text-sm font-bold ${t.text}`}>{cur} {fmtNum(data.outstanding)}</p>
                        ))}
                      </div>
                    ) : (
                      <p className={`text-lg font-bold ${t.text}`}>0.00</p>
                    )}
                    <p className={`text-xs ${t.subtext}`}>{loanSummary.active_loans_count} active</p>
                  </div>
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-purple-50'}`}>
                    <div className="flex items-center gap-1.5 mb-1">
                      <ArrowDownLeft className={`w-3.5 h-3.5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                      <span className={`text-xs font-medium ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>Borrowings</span>
                    </div>
                    {loanSummary.currency_breakdown_borrowings && Object.keys(loanSummary.currency_breakdown_borrowings).length > 0 ? (
                      <div className="space-y-0.5">
                        {Object.entries(loanSummary.currency_breakdown_borrowings).map(([cur, data]) => (
                          <p key={cur} className={`text-sm font-bold ${t.text}`}>{cur} {fmtNum(data.outstanding)}</p>
                        ))}
                      </div>
                    ) : (
                      <p className={`text-lg font-bold ${t.text}`}>0.00</p>
                    )}
                    <p className={`text-xs ${t.subtext}`}>{loanSummary.active_borrowings_count} active</p>
                  </div>
                </div>
                {/* Per-Person Donut Chart */}
                {loanSummary.per_person_breakdown && loanSummary.per_person_breakdown.length > 0 && (
                  <div className="mt-4" data-testid="loan-person-chart">
                    <p className={`text-xs font-medium mb-2 ${t.subtext}`}>Outstanding by Person</p>
                    <div className="flex items-center gap-2">
                      <div className="w-28 h-28 flex-shrink-0">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={loanSummary.per_person_breakdown} dataKey="outstanding" nameKey="person"
                              cx="50%" cy="50%" innerRadius={28} outerRadius={50} paddingAngle={2} strokeWidth={0}>
                              {loanSummary.per_person_breakdown.map((_, i) => (
                                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v) => fmtNum(v)} contentStyle={{ fontSize: '11px', borderRadius: '8px' }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex-1 space-y-1 min-w-0">
                        {loanSummary.per_person_breakdown.slice(0, 5).map((entry, i) => (
                          <div key={`${entry.person}-${entry.type}`} className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                            <span className={`text-xs truncate flex-1 ${t.text}`}>{entry.person}</span>
                            <span className={`text-xs font-mono font-medium flex-shrink-0 ${t.text}`}>{entry.currency} {entry.outstanding.toFixed(0)}</span>
                          </div>
                        ))}
                        {loanSummary.per_person_breakdown.length > 5 && (
                          <p className={`text-[10px] ${t.subtext}`}>+{loanSummary.per_person_breakdown.length - 5} more</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {isGlass && (
                  <div className={`mt-3 h-1 rounded-full bg-gradient-to-r from-amber-500 via-orange-500 to-red-400 opacity-60`} />
                )}
              </Card>

              {/* Monthly Loan Trend Chart */}
              {loanTrend.length > 0 && loanTrend.some(m => m.loans > 0 || m.borrowings > 0) && (
                <Card className={`p-5 ${t.cardBg} transition-all duration-300`} data-testid="loan-trend-chart">
                  <div className="flex items-center gap-2 mb-4">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-blue-500/20' : 'bg-blue-50'}`}>
                      <TrendingUp className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                    </div>
                    <h3 className={`text-sm font-semibold ${t.text}`}>Lending vs Borrowing Trend</h3>
                  </div>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={loanTrend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'} />
                        <XAxis dataKey="month" tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} tickLine={false} axisLine={false} />
                        <Tooltip
                          contentStyle={{
                            fontSize: '11px', borderRadius: '8px',
                            backgroundColor: isDark ? '#1e293b' : '#fff',
                            border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                            color: isDark ? '#e2e8f0' : '#1e293b',
                          }}
                          formatter={(v) => `$${v.toLocaleString()}`}
                        />
                        <Legend wrapperStyle={{ fontSize: '11px' }} />
                        <Bar dataKey="loans" name="Loans (Lent)" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="borrowings" name="Borrowings" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  {isGlass && (
                    <div className={`mt-3 h-1 rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-400 opacity-60`} />
                  )}
                </Card>
              )}
            </div>
          )}

          {/* Quick Actions */}
          <Card className={`p-6 ${t.accent}`}>
            <h2 className={`text-xl font-bold ${t.text} mb-4`}>Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <a
                href="/pos"
                className={`p-4 rounded-lg transition-all ${t.quickCard}`}
                data-testid="quick-action-pos"
              >
                <ShoppingBag className={`w-8 h-8 ${isDark ? 'text-emerald-400' : 'text-emerald-600'} mb-2`} />
                <h3 className={`font-semibold ${t.text}`}>New Sale</h3>
                <p className={`text-sm ${t.subtext}`}>Process a transaction</p>
              </a>
              <a
                href="/inventory"
                className={`p-4 rounded-lg transition-all ${t.quickCard}`}
                data-testid="quick-action-inventory"
              >
                <Package className={`w-8 h-8 ${isDark ? 'text-blue-400' : 'text-blue-600'} mb-2`} />
                <h3 className={`font-semibold ${t.text}`}>Add Product</h3>
                <p className={`text-sm ${t.subtext}`}>Manage inventory</p>
              </a>
              <a
                href="/quotations"
                className={`p-4 rounded-lg transition-all ${t.quickCard}`}
                data-testid="quick-action-quotation"
              >
                <FileText className={`w-8 h-8 ${isDark ? 'text-purple-400' : 'text-purple-600'} mb-2`} />
                <h3 className={`font-semibold ${t.text}`}>New Quotation</h3>
                <p className={`text-sm ${t.subtext}`}>Create estimate</p>
              </a>
            </div>
          </Card>
        </div>

        {/* Customer List Dialog */}
        <Dialog open={showCustomers} onOpenChange={setShowCustomers}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Customer List ({customers.length})</DialogTitle>
            </DialogHeader>

            <div className="space-y-3">
              {customers.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                  <p>No customers yet</p>
                  <p className="text-sm mt-1">Customers will appear here after creating invoices or quotations</p>
                </div>
              ) : (
                customers.map((customer) => (
                  <Card key={customer.id} className="p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900 mb-1">{customer.name}</h3>
                        {customer.email && (
                          <p className="text-sm text-slate-600 mb-1">{customer.email}</p>
                        )}
                        {customer.phone && (
                          <p className="text-sm text-slate-600">{customer.phone}</p>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">
                        {new Date(customer.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
